import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { User } from '../../types';
import authService from '../../services/api/authService';
import tokenManager from '../../utils/tokenManager';
import { logger } from '../../utils/logger';
import { clearReturnTo } from '../../utils/returnTo';

// 認証状態の型定義
interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// tokenManagerを使用してリフレッシュトークンの存在を確認
const hasRefreshToken = tokenManager.hasRefreshToken();
logger.log('AuthSlice: Initializing state', {
  hasToken: tokenManager.hasValidToken(),
  hasRefreshToken, 
  locationPath: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
});

// トークンとリフレッシュトークンを取得
const token = tokenManager.getToken();
const refreshToken = tokenManager.getRefreshToken();

// 初期状態
const initialState: AuthState = {
  user: null,
  token: token, // ストレージから取得したトークン
  // トークンがある、またはリフレッシュトークンがある場合は認証済みとみなす
  isAuthenticated: !!(token || refreshToken),
  isLoading: false,
  error: null
};

// 初期状態のログを出力
logger.log('AuthSlice: Initializing state', {
  hasToken: !!token,
  tokenLength: token?.length,
  hasRefreshToken: !!refreshToken, 
  refreshTokenLength: refreshToken?.length,
  isAuthenticated: initialState.isAuthenticated,
  locationPath: typeof window !== 'undefined' ? window.location.pathname : 'unknown'
});

// 管理者ログイン用の非同期アクション
export const adminLogin = createAsyncThunk(
  'auth/adminLogin',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.adminLogin(credentials);
      // トークンマネージャーでトークンを保存
      if (response.token) {
        tokenManager.saveToken(response.token);
      }
      
      // リフレッシュトークンも保存
      if (response.refresh_token) {
        tokenManager.saveRefreshToken(response.refresh_token);
        logger.log('authSlice: 管理者ログイン時のリフレッシュトークンを保存しました', {
          length: response.refresh_token.length
        });
      } else {
        console.warn('authSlice: 管理者ログイン応答にリフレッシュトークンがありません');
      }
      
      // 管理者データもトークンマネージャーに保存
      if (response.admin) {
        tokenManager.saveUserData(response.admin);
      }
      
      // 管理者情報をユーザー形式に変換して返す
      const adminUser: User = {
        id: response.admin?.id || 0,
        name: response.admin?.name || '',
        email: response.admin?.email || '',
        role: 'admin',
        created_at: response.admin?.created_at,
        updated_at: response.admin?.updated_at,
      };
      
      return {
        user: adminUser,
        token: response.token,
        refresh_token: response.refresh_token
      };
    } catch (error: any) {
      let errorMessage = 'ログインに失敗しました';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ユーザーログイン用の非同期アクション
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      // AuthResponse型をそのまま返す
      return {
        user: response.user,
        token: response.token,
        refresh_token: response.refresh_token
      };
    } catch (error: any) {
      let errorMessage = 'ログインに失敗しました';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// 新規ユーザー登録用の非同期アクション
export const register = createAsyncThunk(
  'auth/register',
  async (userData: { name: string; email: string; password: string; password_confirmation: string }, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return {
        user: response.user,
        token: response.token,
        refresh_token: response.refresh_token
      };
    } catch (error: any) {
      let errorMessage = 'ユーザー登録に失敗しました';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ユーザー情報取得非同期アクション
export const getUserProfile = createAsyncThunk(
  'auth/getUserProfile',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const token = state.auth.token;

    if (!token) {
      return rejectWithValue('認証トークンがありません');
    }

    try {
      const userData = await authService.getCurrentUser();
      return userData;
    } catch (error: any) {
      let errorMessage = 'プロフィール取得に失敗しました';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// ログアウト用の非同期アクション
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    // 意図的なログアウトでは元いた画面への戻り先を捨てる
    // (次に別のユーザーがログインしたとき、前の利用者の画面へ飛ばさない)
    clearReturnTo();
    try {
      await authService.logout();
      return;
    } catch (error: any) {
      // エラーがあってもトークンは削除する
      tokenManager.clearTokens(false);
      let errorMessage = 'ログアウトに失敗しました';
      if (error.response) {
        errorMessage = error.response.data?.message || errorMessage;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// 認証スライス
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.isAuthenticated = true;
      // トークンマネージャーに保存
      tokenManager.saveToken(action.payload);
    },
    // リフレッシュトークンをセットするアクション（新規追加）
    setRefreshToken: (state, action: PayloadAction<string>) => {
      // stateには保存しないがトークンマネージャーには保存
      tokenManager.saveRefreshToken(action.payload);
      // リフレッシュトークンがあれば認証状態に
      state.isAuthenticated = true;
    }
  },
  extraReducers: (builder) => {
    // 管理者ログイン処理
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.error = null;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      });
      
    // ログイン処理
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.error = null;
        
        // リフレッシュトークンが返ってきた場合は保存
        if (action.payload.refresh_token) {
          // トークンマネージャーにリフレッシュトークンを保存
          tokenManager.saveRefreshToken(action.payload.refresh_token);
          logger.log('authSlice: リフレッシュトークンを保存しました', {
            length: action.payload.refresh_token.length
          });
        } else {
          console.warn('authSlice: ログイン応答にリフレッシュトークンがありません');
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      });

    // 新規ユーザー登録処理
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user || null;
        state.token = action.payload.token;
        state.error = null;
        
        // リフレッシュトークンが返ってきた場合は保存
        if (action.payload.refresh_token) {
          // トークンマネージャーにリフレッシュトークンを保存
          tokenManager.saveRefreshToken(action.payload.refresh_token);
          logger.log('authSlice: 新規登録時のリフレッシュトークンを保存しました', {
            length: action.payload.refresh_token.length
          });
        } else {
          console.warn('authSlice: 新規登録応答にリフレッシュトークンがありません');
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // ユーザープロフィール取得処理
    builder
      .addCase(getUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        
        // エラーログを出すが、トークンは削除せず、リダイレクトもしない
        console.warn('ユーザープロファイル取得失敗:', action.payload);
        
        // リフレッシュトークンがある場合は、認証状態を維持
        if (tokenManager.hasRefreshToken()) {
          logger.log('リフレッシュトークンが存在するため認証状態を維持します');
          // 認証状態は維持するが、取得失敗したユーザーデータはnullのまま
        } else {
          // リフレッシュトークンがない場合のみログアウト
          state.isAuthenticated = false;
          state.token = null;
          state.user = null;
        }
      });

    // ログアウト処理
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      });
  }
});

export const { clearError, setError, setUser, setToken, setRefreshToken } = authSlice.actions;

export default authSlice.reducer;
